import { useEffect, useRef, useState } from "react";
import {
  ExcelExport,
  ExcelExportColumn,
} from "@progress/kendo-react-excel-export";
import {
  Grid,
  GridCellProps,
  GridColumn,
  GridColumnMenuFilter,
  GridColumnMenuProps,
  GridDataStateChangeEvent,
  GridExpandChangeEvent,
  GridNoRecords,
} from "@progress/kendo-react-grid";

import {
  setExpandedState,
  setGroupIds,
} from "@progress/kendo-react-data-tools";
import { DataResult, process, State } from "@progress/kendo-data-query";

import {
  DeleteModelDialog,
  DeletePostDataInfo,
  DeleteStatus,
} from "../../../core/delete-model-dialog.component";
import { GridCommandCell } from "../../../core/grid-command-cell.component";
import { GridActionComponent } from "../../../core/action.title.component";
import { LoadingIndicator } from "../../../core/loading-indicator.component";
import { API_END_POINT } from "../../../constants";
import { SubmitStatus } from "../../../core/formik-model-dialog.component";

import { useGetPurchaseList } from "../../../apiclient/transaction/useGetPurchaseListApi";
import TalukModel from "../../../models/masters/taluk-model";
import AgentOrderEntryPage from "./purchase-entry";
import ZoneCounterModel from "../../../models/transaction/zonecounter-model";
import { PurchaseModel } from "../../../models/transaction/purchase-model";
import PurchaseEntryPage from "./purchase-entry";
import PurchaseInvoiceHistoryModel from "../../../models/transaction/purchasehistory-model";
import { getDataWithToken, getEndPoint } from "../../../Utils/network.client";

export const addOrUpdateArrayItem = (data: any[], item: any, key: string) => {
  const newData = data ? [...data] : [];
  const index = data?.findIndex((d) => d[key] === item[key]);
  if (index > -1) {
    newData.splice(index, 1, item);
  } else {
    newData.push(item);
  }
  return newData;
};

const PurchasePage: React.FC = () => {
  const initialDataState: State = {
    take: 10,
    skip: 0,
    group: [],
  };

  const [dataState, setDataState] = useState<State>(initialDataState);
  const [deletePurchaseData, setDeletePurchaseData] = useState<DeletePostDataInfo>();
  const [collapsedState, setCollapsedState] = useState<string[]>([]);
  const [resultData, setResultData] = useState<DataResult>();
  const [editModal, setEditModal] = useState<boolean>(false);
  /*   const [editData, setEditdata] = useState<PurchaseModel>(null); */
  const [isRefreshing, setIsRefreshing] = useState(false);

  const [editData, setEditData] = useState<PurchaseInvoiceHistoryModel | null>(null);

  const exportExcel = useRef<ExcelExport | null>(null);
  const [activePage, setActivePage] = useState<"list" | "entry">("list");

  const {
    data: result,
    isLoading,
    isError,
    refetch: reloadPurchase,
  } = useGetPurchaseList();

  reloadPurchase();

  const processWithGroups = (data: PurchaseInvoiceHistoryModel[], dataState: State) => {
    const groups = dataState.group;
    dataState.group = groups;
    const newDataState = process(data, dataState);
    setGroupIds({ data: newDataState.data, group: dataState.group });
    return newDataState;
  };

  const fetchData = async () => {
    try {
      setIsRefreshing(true);
      const { data, error } = await reloadPurchase();
      if (error) {
        console.error("Error reloading:", error);
      }
    } catch (err) {
      console.error("Unexpected error:", err);
    } finally {
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    setEditData(null);
    reloadPurchase();
    fetchData();
    if (result) {
      setResultData(processWithGroups(result, initialDataState));
    }
  }, [result]);

  const newData = resultData
    ? setExpandedState({
      data: resultData.data,
      collapsedIds: collapsedState,
    })
    : [];

  /*  const onSaveComplete = (status: SubmitStatus, data: PurchaseInvoiceHistoryModel) => {
     setEditData(null);
     setEditModal(false);
     if (status === SubmitStatus.Completed) {
       reloadPurchase();
       setResultData(processWithGroups(newData, dataState));
     }
   }; */

  const excelExport = () => {
    if (exportExcel.current !== null) {
      exportExcel.current.save();
    }
  };

  const onAddClick = () => {
    setEditModal(true);
    setEditData(new PurchaseInvoiceHistoryModel());
    setActivePage("entry");
  };

  const onEdit = (data: PurchaseInvoiceHistoryModel) => {
    const ordDate = new Date(data.orderDate);
    const updatedDataItem = { ...data, orderDate2: ordDate };
    setEditModal(true);
    setEditData(updatedDataItem);
    setActivePage("entry");
    console.log("edit", editData);
  };

  const handleCancelEntry = () => {
    reloadPurchase();
    setEditData(null);
    setActivePage("list"); // return to list page
  };



  const onRemove = (data: PurchaseInvoiceHistoryModel) => {
    const { purchaseInvoiceMainId } = data;
    setDeletePurchaseData({
      data,
      route: `PurchaseInvoice/DeletePurchaseInvoice/${purchaseInvoiceMainId}`,
      endpoint: API_END_POINT.ORDER,
    });
    reloadPurchase();
  };

  const confirmDelete = async (dataItem: any) => {
    const confirmed = window.confirm("Do you want to delete this invoice?");
    if (confirmed) {
      const getDeleteInvoiceUrl = getEndPoint(API_END_POINT.ORDER) + `/api/PurchaseInvoice/DeletePurchaseInvoice/${dataItem.purchaseInvoiceMainId}`;
      try {
        const response = await getDataWithToken<any>(getDeleteInvoiceUrl);
        const res = response?.data?.result;
        if (res) {
          reloadPurchase();
        } else {
          console.error("No invoice data found.");
        }
      } catch (error) {
        console.error('Error deleting invoice:', error);
      }
    }
    //setSelectedInvoice(null); // Close dialog
  };

  const onDeleteComplete = (status: DeleteStatus, data: PurchaseInvoiceHistoryModel) => {
    if (status === DeleteStatus.Completed) {
      reloadPurchase();
      setResultData(processWithGroups(result, dataState));
    }
    setDeletePurchaseData(null);
  };

  const ColumnMenu = (props: GridColumnMenuProps) => (
    <div>
      <GridColumnMenuFilter {...props} expanded={true} />
    </div>
  );

  const CommandCell = (props: GridCellProps) => (
    <GridCommandCell
      {...props}
      edit={onEdit}
      remove={confirmDelete}
      add={onAddClick}
      dataItem={props.dataItem}
      idPropertyName="purchaseInvoiceMainId"
    />
  );

  const onSearch = (e: any) => {
    const searchKey: string = e.target.value;
    if (searchKey?.length > 0) {
      const filteredData = result?.filter((p) =>
        p?.invoiceNumber?.toLowerCase().includes(searchKey.toLowerCase())
      );
      setResultData(process(filteredData, dataState));
    } else {
      setResultData(process(result, dataState));
    }
  };

  const expandChange = (event: GridExpandChangeEvent) => {
    const item = event.dataItem;
    if (item.groupId) {
      const newCollapsedIds = !event.value
        ? [...collapsedState, item.groupId]
        : collapsedState.filter((groupId) => groupId !== item.groupId);
      setCollapsedState(newCollapsedIds);
    }
  };

  const dataStateChange = (event: GridDataStateChangeEvent) => {
    const newDataState = processWithGroups(result, event.dataState);
    setResultData(newDataState);
    setDataState(event.dataState);
  };

  if (isError) {
    return <div><h4>Error Loading Page...</h4></div>;
  }

  return (
    <div
      style={{ minHeight: "600px" }}
      className="card p-3 border-0 m-3 sw-shadow radius-15"
    >
      {activePage === "entry" && editData ? (
        <PurchaseEntryPage
          data={editData}
          onClose={handleCancelEntry}
          setActivePage={setActivePage}
        />
      ) : (
        // {editModal ? (
        //   <PurchaseEntryPage
        //     data={editData}
        //     onClose={() => {
        //       setEditModal(false);
        //       setEditData(null);
        //     }}
        //   />
        // ) : (
        <>
          <GridActionComponent
            onSearchClick={onSearch}
            addButtonClick={onAddClick}
            excelExportClick={excelExport}
            refreshClick={fetchData}
            title="PURCHASE"
            addButtonTooltip="Add Purchase"
          />

          {deletePurchaseData && (
            <DeleteModelDialog
              title="Delete Purchase"
              deleteData={deletePurchaseData}
              onClose={onDeleteComplete}
            >
              <div>
                Are you sure you want to delete Purchase:{" "}
                <strong>{deletePurchaseData.data.invoiceNumber}</strong>?
              </div>
            </DeleteModelDialog>
          )}

          {isRefreshing || isLoading ? (
            <LoadingIndicator isLoading={isRefreshing} />
          ) : (
            <>
              <Grid
                data={newData}
                pageable={{ pageSizes: [10, 20, 50, 100] }}
                total={resultData?.total}
                groupable={true}
                sortable={true}
                onDataStateChange={dataStateChange}
                {...dataState}
                onExpandChange={expandChange}
                resizable={true}
                columnVirtualization={false}
              >
                {/*  <GridColumn field="name" title="Name" columnMenu={ColumnMenu} /> */}
                <GridColumn
                  width={"200px"}
                  field="orderDate"
                  title="Order Date"
                  columnMenu={ColumnMenu}
                />
                <GridColumn
                  width={"200px"}
                  field="deliveryDate"
                  title="Delivery Date"
                  columnMenu={ColumnMenu}
                />

                <GridColumn
                  width={"200px"}
                  field="invoiceNumber"
                  title="Invoice No"
                //columnMenu={ColumnMenu}
                />
                <GridColumn
                  width={"180px"}
                  field="orderQty"
                  title="Qty"
                //columnMenu={ColumnMenu}
                />

                <GridColumn
                  width={"180px"}
                  field="netAmount"
                  title="Amount"
                //columnMenu={ColumnMenu}
                />

                <GridColumn cell={CommandCell} title="Action" groupable={true} />
                <GridNoRecords>
                  <div className="mt-2" style={{ width: "100%", height: "500px" }}>
                    <center><h5>No data found</h5></center>
                  </div>
                </GridNoRecords>
              </Grid>

              <ExcelExport fileName={"Purchase_List"} data={result} ref={exportExcel}>
                <ExcelExportColumn width={100} field="name" title=" Name" />
              </ExcelExport>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default PurchasePage;
